import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { AccountsService } from '../../accounts.service';
import { AccountingTaxCode } from '../../accounting-tax-code.model';
import { ManualEntryPolicy, ManualEntryReasonCode } from '../../manual-entry-control.model';
import { SessionStorageService } from '../../../services/session-storage.service';
import { PaymentTerm } from '../../../order/models/payment-term.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { NewFinanceNoteService, NotePostingSettings } from '../../notes/new-finance-note.service';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-manual-entry-controls',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, CheckboxModule, DialogModule, IftaLabelModule, InputGroupModule, InputGroupAddonModule, InputNumberModule, InputTextModule, SelectModule, TableModule, TabsModule, ToastModule, PageShellComponent, PageHeroComponent, PageDataPanelComponent],
    providers: [MessageService],
    templateUrl: './manual-entry-controls.component.html',
    styleUrls: ['./manual-entry-controls.component.css']
})
export class ManualEntryControlsComponent implements OnInit {
    readonly controlTabs = ['reason-codes', 'policies', 'tax-codes', 'payment-terms'] as const;
    documentTypes = ['MANUAL_RECEIPT', 'MANUAL_VOUCHER', 'MANUAL_RECEIVABLE_LINE', 'MANUAL_PAYABLE_LINE', 'MANUAL_CUSTOMER_INVOICE', 'NON_PO_VENDOR_BILL', 'STANDALONE_DEBIT_NOTE', 'STANDALONE_CREDIT_NOTE', 'ALLOCATION_ADJUSTMENT'];
    sourceTypes = ['MANUAL', 'SYSTEM', 'MIGRATED'];
    policies: ManualEntryPolicy[] = [];
    reasonCodes: ManualEntryReasonCode[] = [];
    taxCodes: AccountingTaxCode[] = [];
    paymentTerms: PaymentTerm[] = [];
    noteAccounts: ChartOfAccounts[] = [];
    noteSettings: NotePostingSettings = { receivableAccountId: null, payableAccountId: null,
        outputVatAccountId: null, inputVatAccountId: null, claimSuspenseAccountId: null };
    savingNoteSettings = false;
    reasonDraft: ManualEntryReasonCode = { documentType: 'MANUAL_RECEIPT', reasonCode: '', description: '', requiresAttachment: false, requiresApproval: true, active: true };
    policyDraft: ManualEntryPolicy = { owningCompanyId: 0, documentType: 'MANUAL_RECEIPT', sourceType: 'MANUAL', attachmentRequired: false, approvalRequired: true, active: true };
    taxCodeDraft: AccountingTaxCode = { taxCode: '', description: '', taxRate: 15, taxCategory: 'STANDARD', active: true };
    paymentTermDraft: PaymentTerm = { paymentTermID: 0, name: '', description: '', days: 0, createDate: new Date(), updateDate: new Date() };
    activeTab: ManualEntryControlTab = 'reason-codes';
    reasonListDocumentType = 'MANUAL_RECEIPT';
    activeDialog: ManualEntryControlDialog | null = null;
    dialogVisible = false;
    private reasonDraftBeforeEdit: ManualEntryReasonCode | null = null;
    private policyDraftBeforeEdit: ManualEntryPolicy | null = null;
    private taxCodeDraftBeforeEdit: AccountingTaxCode | null = null;
    private paymentTermDraftBeforeEdit: PaymentTerm | null = null;

    constructor(private accountsService: AccountsService, private sessionStorage: SessionStorageService, private messageService: MessageService,
                private chartAccounts: ChartOfAccountsService, private notes: NewFinanceNoteService) {}

    ngOnInit(): void {
        this.policyDraft.owningCompanyId = this.sessionStorage.getCompany()?.id || 0;
        this.reasonDraft.owningCompanyId = this.policyDraft.owningCompanyId;
        this.taxCodeDraft.owningCompanyId = this.policyDraft.owningCompanyId;
        this.loadReasonCodes();
        this.loadPolicies();
        this.loadTaxCodes();
        this.loadPaymentTerms();
        this.loadNoteSettings();
    }

    loadReasonCodes(): void {
        if (!this.policyDraft.owningCompanyId || !this.reasonListDocumentType) return;
        this.accountsService.getManualEntryReasonCodes(this.policyDraft.owningCompanyId, this.reasonListDocumentType).subscribe(codes => this.reasonCodes = codes);
    }

    loadPolicies(): void {
        if (!this.policyDraft.owningCompanyId) return;
        this.accountsService.getManualEntryPolicies(this.policyDraft.owningCompanyId).subscribe(policies => this.policies = policies);
    }

    loadTaxCodes(): void {
        this.accountsService.getAccountingTaxCodes(this.policyDraft.owningCompanyId).subscribe(codes => this.taxCodes = codes);
    }

    loadPaymentTerms(): void {
        this.accountsService.getPaymentTerms(this.policyDraft.owningCompanyId || undefined).subscribe(terms => this.paymentTerms = terms);
    }

    loadNoteSettings(): void {
        const companyId = this.policyDraft.owningCompanyId;
        if (!companyId) return;
        forkJoin({ settings: this.notes.getPostingSettings(companyId), accounts: this.chartAccounts.getAllChartOfAccountsByCompany(companyId, false) })
            .subscribe({ next: data => { this.noteSettings = data.settings; this.noteAccounts = data.accounts.filter(account => account.isActive && !account.deprecated); },
                error: () => this.messageService.add({ severity: 'error', summary: 'Note accounts unavailable', detail: 'Could not load note posting accounts.' }) });
    }

    saveNoteSettings(): void {
        this.savingNoteSettings = true;
        this.notes.savePostingSettings(this.policyDraft.owningCompanyId, this.noteSettings).subscribe({
            next: value => { this.noteSettings = value; this.savingNoteSettings = false; this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Note posting accounts saved.' }); },
            error: err => { this.savingNoteSettings = false; this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save note posting accounts.' }); }
        });
    }

    saveReason(): void {
        this.reasonDraft.requiresAttachment = false;
        this.accountsService.saveManualEntryReasonCode(this.reasonDraft).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Reason code saved.' });
                this.reasonListDocumentType = this.reasonDraft.documentType;
                this.loadReasonCodes();
                this.resetReasonDraft();
                this.closeDialog();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save reason code.' })
        });
    }

    savePolicy(): void {
        this.policyDraft.attachmentRequired = false;
        this.accountsService.saveManualEntryPolicy(this.policyDraft).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Manual-entry policy saved.' });
                this.loadPolicies();
                this.resetPolicyDraft();
                this.closeDialog();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save policy.' })
        });
    }

    saveTaxCode(): void {
        this.accountsService.saveAccountingTaxCode(this.taxCodeDraft).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Tax code saved.' });
                this.resetTaxCodeDraft();
                this.loadTaxCodes();
                this.closeDialog();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save tax code.' })
        });
    }

    savePaymentTerm(): void {
        if (!this.paymentTermDraft.name?.trim() || !this.paymentTermDraft.description?.trim()
            || this.paymentTermDraft.days == null || this.paymentTermDraft.days < 0) {
            this.messageService.add({ severity: 'error', summary: 'Payment term incomplete', detail: 'Name, description, and a non-negative day count are required.' });
            return;
        }
        this.accountsService.savePaymentTerm(this.paymentTermDraft, this.policyDraft.owningCompanyId || undefined).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Payment term saved.' });
                this.resetPaymentTermDraft();
                this.loadPaymentTerms();
                this.closeDialog();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Save failed', detail: err?.error?.message || 'Could not save payment term.' })
        });
    }

    archivePaymentTerm(term: PaymentTerm): void {
        if (!term.paymentTermID || !window.confirm(`Archive payment term "${term.name}"? Existing documents will keep their snapshots.`)) return;
        this.accountsService.archivePaymentTerm(term.paymentTermID, this.policyDraft.owningCompanyId || undefined).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: 'Archived', detail: 'Payment term archived.' });
                this.loadPaymentTerms();
            },
            error: err => this.messageService.add({ severity: 'error', summary: 'Archive failed', detail: err?.error?.message || 'Could not archive payment term.' })
        });
    }

    onTabChange(tab: string | number): void {
        if (typeof tab === 'string' && this.controlTabs.includes(tab as ManualEntryControlTab)) {
            this.activeTab = tab as ManualEntryControlTab;
        }
    }

    openActiveDialog(): void {
        const dialogByTab: Record<ManualEntryControlTab, ManualEntryControlDialog> = {
            'reason-codes': 'reason',
            'policies': 'policy',
            'tax-codes': 'tax-code',
            'payment-terms': 'payment-term'
        };
        this.openDialog(dialogByTab[this.activeTab]);
    }

    openDialog(dialog: ManualEntryControlDialog): void {
        this.clearEditSnapshots();
        if (dialog === 'reason') this.resetReasonDraft();
        if (dialog === 'policy') this.resetPolicyDraft();
        if (dialog === 'tax-code') this.resetTaxCodeDraft();
        if (dialog === 'payment-term') this.resetPaymentTermDraft();
        this.activeDialog = dialog;
        this.dialogVisible = true;
    }

    editReasonCode(code: ManualEntryReasonCode): void {
        this.reasonDraft = { ...code, requiresAttachment: false };
        this.reasonDraftBeforeEdit = { ...this.reasonDraft };
        this.activeDialog = 'reason';
        this.dialogVisible = true;
    }

    editPolicy(policy: ManualEntryPolicy): void {
        this.policyDraft = { ...policy, attachmentRequired: false };
        this.policyDraftBeforeEdit = { ...this.policyDraft };
        this.activeDialog = 'policy';
        this.dialogVisible = true;
    }

    editTaxCode(code: AccountingTaxCode): void {
        this.taxCodeDraft = { ...code };
        this.taxCodeDraftBeforeEdit = { ...this.taxCodeDraft };
        this.activeDialog = 'tax-code';
        this.dialogVisible = true;
    }

    editPaymentTerm(term: PaymentTerm): void {
        this.paymentTermDraft = { ...term };
        this.paymentTermDraftBeforeEdit = { ...this.paymentTermDraft };
        this.activeDialog = 'payment-term';
        this.dialogVisible = true;
    }

    closeDialog(): void {
        this.dialogVisible = false;
        this.activeDialog = null;
        this.clearEditSnapshots();
    }

    resetActiveDraft(): void {
        if (this.activeDialog === 'reason') {
            this.reasonDraft = this.reasonDraftBeforeEdit ? { ...this.reasonDraftBeforeEdit } : this.newReasonDraft();
        }
        if (this.activeDialog === 'policy') {
            this.policyDraft = this.policyDraftBeforeEdit ? { ...this.policyDraftBeforeEdit } : this.newPolicyDraft();
        }
        if (this.activeDialog === 'tax-code') {
            this.taxCodeDraft = this.taxCodeDraftBeforeEdit ? { ...this.taxCodeDraftBeforeEdit } : this.newTaxCodeDraft();
        }
        if (this.activeDialog === 'payment-term') {
            this.paymentTermDraft = this.paymentTermDraftBeforeEdit ? { ...this.paymentTermDraftBeforeEdit } : this.newPaymentTermDraft();
        }
    }

    saveActiveDraft(): void {
        if (this.activeDialog === 'reason') this.saveReason();
        if (this.activeDialog === 'policy') this.savePolicy();
        if (this.activeDialog === 'tax-code') this.saveTaxCode();
        if (this.activeDialog === 'payment-term') this.savePaymentTerm();
    }

    canSaveActiveDraft(): boolean {
        if (this.activeDialog === 'reason') return !!this.reasonDraft.documentType && !!this.reasonDraft.reasonCode?.trim() && !!this.reasonDraft.description?.trim();
        if (this.activeDialog === 'policy') return !!this.policyDraft.documentType && !!this.policyDraft.sourceType;
        if (this.activeDialog === 'tax-code') return !!this.taxCodeDraft.taxCode?.trim();
        if (this.activeDialog === 'payment-term') return !!this.paymentTermDraft.name?.trim() && !!this.paymentTermDraft.description?.trim() && this.paymentTermDraft.days != null && this.paymentTermDraft.days >= 0;
        return true;
    }

    get dialogTitle(): string {
        const edit = this.isEditingDialog ? 'Edit' : 'Create';
        if (this.activeDialog === 'reason') return `${edit} Reason Code`;
        if (this.activeDialog === 'policy') return `${edit} Manual Entry Policy`;
        if (this.activeDialog === 'tax-code') return `${edit} Tax Code`;
        if (this.activeDialog === 'payment-term') return `${edit} Payment Term`;
        return 'Manual Entry Control';
    }

    get dialogSubmitLabel(): string {
        return this.isEditingDialog ? 'Update' : 'Create';
    }

    get dialogIcon(): string {
        if (this.activeDialog === 'reason') return 'pi pi-tags';
        if (this.activeDialog === 'policy') return 'pi pi-shield';
        if (this.activeDialog === 'tax-code') return 'pi pi-percentage';
        return 'pi pi-credit-card';
    }

    get dialogEyebrow(): string {
        if (this.activeDialog === 'reason') return 'Approval routing';
        if (this.activeDialog === 'policy') return 'Policy rule';
        if (this.activeDialog === 'tax-code') return 'Tax configuration';
        return 'Commercial terms';
    }

    get dialogSummaryTitle(): string {
        if (this.activeDialog === 'reason') return this.reasonDraft.reasonCode || 'Reason code';
        if (this.activeDialog === 'policy') return this.policyDraft.documentType || 'Manual entry policy';
        if (this.activeDialog === 'tax-code') return this.taxCodeDraft.taxCode || 'Tax code';
        return this.paymentTermDraft.name || 'Payment term';
    }

    get dialogSummaryText(): string {
        if (this.activeDialog === 'reason') return 'Controls the reason a user selects and whether approval is required for that manual entry.';
        if (this.activeDialog === 'policy') return 'Sets the approval rule for a document type and source combination. Supporting documents stay optional.';
        if (this.activeDialog === 'tax-code') return 'Defines the tax treatment used by manual receivable, payable, invoice, and bill lines.';
        return 'Sets a reusable payment schedule for sales, shipments, and accounting documents.';
    }

    get dialogPrimaryFactLabel(): string {
        if (this.activeDialog === 'reason' || this.activeDialog === 'policy') return 'Document type';
        if (this.activeDialog === 'tax-code') return 'Rate';
        return 'Due in';
    }

    get dialogPrimaryFactValue(): string {
        if (this.activeDialog === 'reason') return this.reasonDraft.documentType || '-';
        if (this.activeDialog === 'policy') return this.policyDraft.documentType || '-';
        if (this.activeDialog === 'tax-code') return `${this.taxCodeDraft.taxRate ?? 0}%`;
        return `${this.paymentTermDraft.days ?? 0} days`;
    }

    get dialogSecondaryFactLabel(): string {
        if (this.activeDialog === 'reason' || this.activeDialog === 'policy') return 'Approval';
        if (this.activeDialog === 'tax-code') return 'Category';
        return 'Tenant';
    }

    get dialogSecondaryFactValue(): string {
        if (this.activeDialog === 'reason') return this.reasonDraft.requiresApproval ? 'Required' : 'Not required';
        if (this.activeDialog === 'policy') return this.policyDraft.approvalRequired ? 'Required' : 'Not required';
        if (this.activeDialog === 'tax-code') return this.taxCodeDraft.taxCategory || '-';
        return 'Current company';
    }

    get activeTabAddLabel(): string {
        if (this.activeTab === 'reason-codes') return 'Add Reason Code';
        if (this.activeTab === 'policies') return 'Add Policy';
        if (this.activeTab === 'tax-codes') return 'Add Tax Code';
        return 'Add Payment Term';
    }

    private get isEditingDialog(): boolean {
        if (this.activeDialog === 'reason') return !!this.reasonDraft.reasonCodeId;
        if (this.activeDialog === 'policy') return !!this.policyDraft.policyId;
        if (this.activeDialog === 'tax-code') return !!this.taxCodeDraft.taxCodeId;
        if (this.activeDialog === 'payment-term') return !!this.paymentTermDraft.paymentTermID;
        return false;
    }

    resetReasonDraft(): void {
        this.reasonDraft = this.newReasonDraft();
    }

    resetPolicyDraft(): void {
        this.policyDraft = this.newPolicyDraft();
    }

    resetTaxCodeDraft(): void {
        this.taxCodeDraft = this.newTaxCodeDraft();
    }

    resetPaymentTermDraft(): void {
        this.paymentTermDraft = this.newPaymentTermDraft();
    }

    private newReasonDraft(): ManualEntryReasonCode {
        return { owningCompanyId: this.policyDraft.owningCompanyId, documentType: this.reasonDraft.documentType || 'MANUAL_RECEIPT', reasonCode: '', description: '', requiresAttachment: false, requiresApproval: true, active: true };
    }

    private newPolicyDraft(): ManualEntryPolicy {
        return { owningCompanyId: this.policyDraft.owningCompanyId, documentType: 'MANUAL_RECEIPT', sourceType: 'MANUAL', attachmentRequired: false, approvalRequired: true, active: true };
    }

    private newTaxCodeDraft(): AccountingTaxCode {
        return { owningCompanyId: this.policyDraft.owningCompanyId, taxCode: '', description: '', taxRate: 15, taxCategory: 'STANDARD', active: true };
    }

    private newPaymentTermDraft(): PaymentTerm {
        return { paymentTermID: 0, name: '', description: '', days: 0, createDate: new Date(), updateDate: new Date() };
    }

    private clearEditSnapshots(): void {
        this.reasonDraftBeforeEdit = null;
        this.policyDraftBeforeEdit = null;
        this.taxCodeDraftBeforeEdit = null;
        this.paymentTermDraftBeforeEdit = null;
    }
}

type ManualEntryControlTab = 'reason-codes' | 'policies' | 'tax-codes' | 'payment-terms';
type ManualEntryControlDialog = 'reason' | 'policy' | 'tax-code' | 'payment-term';

